import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, Observable, switchMap } from 'rxjs';
import { TranslationManagerService } from '../../../core/i18n';
import { VchasnoKasaService } from '../../../services/vchasno-kasa.service';
import { IChatItem } from '../../../interfaces';
import { BoardPaymentService } from '../../../services/board-payment.service';
import { StackedSidebarService } from '../../../services/stacked-sidebar.service';
import { BoardV3PaymentFormPayload, SidebarType } from '../../layout/stacked-sidebar/stacked-sidebar.interface';
import { isStalePaymentError } from '../../payments/payment-form/payment-form.component';
import { CardPayment, CardPaymentsBlock, DealPaymentStatus } from '../../payments/payments.types';
import {
  DEAL_PAYMENT_STATUS_STYLES,
  getDealBalance,
  getPaidPercent,
  CopiedMark,
  getPayableInvoice,
  getPaymentDate,
  isInvoiceExpired,
  isPaymentCancelable,
  isPaymentEditable,
  PAYMENT_STATUS_CLASSES,
} from '../../payments/payments.utils';
import { TableV2Action, TableV2Column } from '../../table-v2/table-v2.interface';
import { formatMoneyAmount } from '../price-field.utils';
import { CardItemsTotal } from '../../warehouse/warehouse.types';
import { PaymentFiscalStatusComponent } from '../../payments/payment-fiscal-status/payment-fiscal-status.component';

/**
 * Payments of a deal: what is paid of the deal amount and the list of the payments with the actions their status
 * allows. A change is saved right away and the payments of the card come back; the form opens in a sidebar.
 */
@Component({
  selector: 'app-board-v3-card-payments',
  templateUrl: './card-payments.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardV3CardPaymentsComponent implements OnInit {
  @Input({ required: true }) cardId!: string;
  /** Every payment of the card, the canceled ones too, oldest first */
  @Input() chats: IChatItem[] = [];
  @Input() payments: CardPayment[] = [];
  @Input() itemsTotals: CardItemsTotal[] = [];
  @Input() paidTotal = 0;
  /** Derived by the backend from the paid total and the deal amount, so it is never worked out here */
  @Input() paymentStatus: DealPaymentStatus | null = null;
  /** The deal amount */
  @Input() price = 0;
  /** The currency of the card: the amounts of its payments are in it */
  @Input() currency: string | null = null;
  @Output() changed = new EventEmitter<CardPaymentsBlock>();
  @ViewChildren(PaymentFiscalStatusComponent) private fiscalStatuses!: QueryList<PaymentFiscalStatusComponent>;

  private readonly fiscal = inject(VchasnoKasaService);
  private readonly paymentService = inject(BoardPaymentService);
  private readonly stackedSidebarService = inject(StackedSidebarService);
  private readonly translationManager = inject(TranslationManagerService);
  private readonly destroyRef = inject(DestroyRef);

  /** The payment whose link was just copied */
  private readonly copied = new CopiedMark();
  /** Payments whose request is running: their actions wait for it */
  protected readonly busyIds = new Set<string>();

  private readonly isBusy = (payment: CardPayment): boolean => this.busyIds.has(payment.id);

  protected readonly columns: TableV2Column<CardPayment>[] = [
    { key: 'amount', header: 'board_payments.fields.amount', align: 'right', nowrap: true },
    { key: 'payment_type', header: 'board_payments.fields.payment_type' },
    { key: 'payment_service', header: 'board_payments.fields.payment_service' },
    { key: 'fiscal', header: 'prro.receipt' },
    { key: 'status', header: 'board_payments.fields.status', nowrap: true },
    { key: 'date', header: 'board_payments.fields.date', nowrap: true, cellClass: 'text-slate-600' },
    // The flexible column: it takes what is left of the width and truncates the comment
    { key: 'comment', header: 'board_payments.fields.comment', class: 'w-full max-w-0' },
  ];
  /** A pending payment can be edited, paid or canceled, a paid one only canceled, a canceled one is final */
  protected readonly actions: TableV2Action<CardPayment>[] = [
    {
      icon: 'assignment_return',
      tooltip: 'prro.refund.title',
      visible: (payment) => payment.status === 'paid' && !!payment.fiscal_operation,
      disabled: this.isBusy,
      click: (payment) => this.fiscalStatuses.find((status) => status.payment.id === payment.id)?.openReturn(),
    },
    {
      icon: (payment) => (this.copied.id === payment.id ? 'check' : 'link'),
      tooltip: (payment) => this.invoiceTooltip(payment),
      visible: (payment) => !!getPayableInvoice(payment),
      click: (payment) => this.copyInvoiceLink(payment),
    },
    {
      icon: 'edit',
      tooltip: 'board_payments.card.edit',
      visible: isPaymentEditable,
      disabled: this.isBusy,
      click: (payment) => this.openForm(payment),
    },
    {
      icon: 'check_circle',
      tooltip: 'board_payments.card.mark_paid',
      tone: 'success',
      visible: isPaymentEditable,
      disabled: this.isBusy,
      click: (payment) => this.markPaid(payment),
    },
    {
      icon: 'block',
      tooltip: 'board_payments.card.cancel_payment',
      tone: 'danger',
      visible: isPaymentCancelable,
      disabled: this.isBusy,
      click: (payment) => this.cancel(payment),
    },
  ];
  protected readonly trackById = (payment: CardPayment): string => payment.id;
  protected readonly rowClass = (payment: CardPayment): string =>
    payment.status === 'canceled' || this.isBusy(payment) ? 'opacity-60' : '';

  protected readonly formatMoneyAmount = formatMoneyAmount;
  protected readonly getDealBalance = getDealBalance;
  protected readonly getPaidPercent = getPaidPercent;
  protected readonly getPaymentDate = getPaymentDate;
  protected readonly statusClasses = PAYMENT_STATUS_CLASSES;
  protected readonly dealStatusStyles = DEAL_PAYMENT_STATUS_STYLES;

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => this.copied.destroy());
    // The card opens over any screen, not only over the ones whose route loads the payments texts
    this.translationManager.loadModuleTranslations('payments').pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  protected copyInvoiceLink(payment: CardPayment): void {
    const invoice = getPayableInvoice(payment);
    if (invoice && this.paymentService.copyInvoiceLink(invoice)) this.copied.mark(payment.id);
  }

  private invoiceTooltip(payment: CardPayment): string {
    if (this.copied.id === payment.id) return 'board_payments.invoice.copied';
    const invoice = getPayableInvoice(payment);
    return invoice && isInvoiceExpired(invoice) ? 'board_payments.invoice.copy_expired' : 'board_payments.invoice.copy';
  }

  /** The payment, or a new one */
  protected openForm(payment: CardPayment | null, confirmOnly = false): void {
    const productTotal = this.itemsTotals.find((total) => total.currency === this.currency)?.amount;
    const payload: BoardV3PaymentFormPayload = {
      cardId: this.cardId,
      currency: this.currency,
      payment,
      defaultAmount: productTotal === undefined ? null : Math.max(0, Math.round((productTotal - this.paidTotal) * 100) / 100),
      confirmOnly,
      chats: this.chats,
    };
    this.stackedSidebarService
      .openSidebar({ type: SidebarType.BoardV3PaymentForm, width: '520px' }, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((block?: CardPaymentsBlock) => block && this.changed.emit(block));
  }

  protected markPaid(payment: CardPayment): void {
    if (this.isBusy(payment)) return;
    this.busyIds.add(payment.id);
    this.fiscal
      .availability(this.cardId)
      .pipe(
        finalize(() => this.busyIds.delete(payment.id)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          this.busyIds.delete(payment.id);
          if (result.connected) this.openForm(payment, true);
          else
            this.run(
              payment,
              this.paymentService.updatePayment(payment.id, { status: 'paid' }),
              'board_payments.card.marked_paid',
            );
        },
        error: (error) => this.handleError(error),
      });
  }

  protected reloadFiscal(): void {
    this.paymentService
      .getCardPayments(this.cardId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (block) => this.changed.emit(block), error: (error) => this.handleError(error) });
  }

  protected cancel(payment: CardPayment): void {
    const request$ = this.paymentService
      .confirm('board_payments.card.cancel_confirm', { amount: formatMoneyAmount(payment.amount, this.currency) })
      .pipe(switchMap(() => this.paymentService.updatePayment(payment.id, { status: 'canceled' })));
    this.run(payment, request$, 'board_payments.card.canceled');
  }

  private run(payment: CardPayment, request$: Observable<CardPaymentsBlock>, successKey: string): void {
    if (this.isBusy(payment)) return;

    this.busyIds.add(payment.id);
    request$
      .pipe(
        finalize(() => this.busyIds.delete(payment.id)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (block) => {
          this.paymentService.success(successKey);
          this.changed.emit(block);
        },
        error: (error) => this.handleError(error),
      });
  }

  private handleError(error: unknown): void {
    this.paymentService.error(error);
    if (!isStalePaymentError(this.paymentService.getErrorCode(error))) return;

    // Someone else changed the payment or it is gone: show what the card has now
    this.paymentService
      .getCardPayments(this.cardId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (block) => this.changed.emit(block),
        error: (reloadError) => this.paymentService.error(reloadError),
      });
  }
}
